import { Component, Inject, inject, OnDestroy, OnInit } from '@angular/core';
import { IdentificationService } from './services/identification.service';
import { filter, forkJoin, map, Subscription } from 'rxjs';
import { SessionService } from './services/session.service';
import { ResiliationService } from './services/resiliation.service';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { BreadcrumbComponent, LoaderComponent, LoaderService, MessageType, WINDOW } from '@sfcu/sfc-front-starter';
import { toSignal } from '@angular/core/rxjs-interop';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import Keycloak from 'keycloak-js';
import { HostnameService } from './services/hostname.service';

registerLocaleData(localeFr, 'fr');

const redCssUrls = {
    prod: {
        base: 'https://static.s-sfr.fr/redbysfr/resources/css/base-red-1.0.css',
        plus: 'https://static.s-sfr.fr/redbysfr/resources/css/plus-red.css',
    },
    cms: {
        base: 'https://cms.red-by-sfr.fr/resources/css/base-red-1.0.css',
        plus: 'https://cms.red-by-sfr.fr/resources/css/plus-red.css',
    }

}
const sfrCssUrls = {
    prod: {
        base: 'https://static.s-sfr.fr/resources/css/base-1.1.css',
        plus: 'https://static.s-sfr.fr/resources/css/plus.css',
    },
    cms: {
        base: 'https://cms.sfr.fr/resources/css/base-1.1.css',
        plus: 'https://cms.sfr.fr/resources/css/plus.css',
    }

}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [RouterOutlet, BreadcrumbComponent, LoaderComponent],
    standalone: true
})
export class AppComponent implements OnInit, OnDestroy {
    public isRed: boolean;
    public isMobile: boolean;
    public isHome: boolean;
    private readonly loaderService = inject(LoaderService);

    private selectedLine$: Subscription;
    isLoading = toSignal(this.loaderService.loading$, { initialValue: false });

    constructor(private identificationService: IdentificationService,
                private resiliationService: ResiliationService,
                private sessionService: SessionService,
                private hostnameService: HostnameService,
                private route: ActivatedRoute,
                private keycloak: Keycloak,
                @Inject(WINDOW) private window: Window) {
    }

    ngOnInit() {
        const espaceSfrUrlProd = 'espacesfr.sfr.fr';
        const espaceSfrUrlPfv = 'espace-client-pack.espacesfr.sfr.fr';
        const authServerUrl = this.keycloak.authServerUrl;
        const isDistrib =  (authServerUrl?.includes(espaceSfrUrlProd)
                || authServerUrl?.includes(espaceSfrUrlPfv)) ?? false;

        this.isRed = this.hostnameService.isRed();

        forkJoin({
            selectedLine: this.identificationService.getSelection(isDistrib),
            infosClient: this.resiliationService.getInfosClient().pipe(
                map(ric => ric.reponse?.client),
                filter(client => client !== undefined)
            ),
        }).pipe().subscribe({
            next: result => {
                const { box4G, profilPSW, resilieData, libellePlanTarifaire } = result.selectedLine;
                const { srr, tiers, pdv } = result.infosClient;

                this.identificationService.setSelectedLineNumber(result.selectedLine?.numero!);
                if (profilPSW !== undefined) {
                    this.isRed = profilPSW.indexOf('RED') !== -1 || this.isRed;
                    this.isMobile = profilPSW.includes('MOBILE') || profilPSW.includes('HOME');
                    this.isHome = profilPSW.includes('HOME');
                    this.sessionService.setUserData({
                        isBox5G: libellePlanTarifaire?.toLowerCase().includes('box 5g') ?? false,
                        isMobileResilie: profilPSW?.includes('MOBILE_RESILIE') ?? false,
                        isMobileAbonneRed: resilieData?.profilPSW.includes('MOBILE_ABONNE_RED') ?? false,
                        isMobileAbonneSfr: (resilieData?.profilPSW.includes('MOBILE_ABONNE') && !resilieData?.profilPSW.includes('MOBILE_ABONNE_RED')) ?? false,
                        isInternetResilie: profilPSW.includes('INTERNET_RESILIE') ?? false,
                        isSrr: srr,
                        isTiers: tiers,
                        isRed: this.isRed,
                        isMobile: this.isMobile,
                        isHome: this.isHome,
                        isBox4G: box4G,
                        isCC: false,
                        isDistrib,
                        pdv
                    });
                    this.applyStylings(this.isRed ? 'red' : 'sfr');
                } else {
                    this.isRed = false;
                    this.isMobile = false;
                    this.applyStylings(this.isRed ? 'red' : 'sfr');
                }
            },
            error: () => {
                this.sessionService.setLoaded(true);
                this.isRed = false;
                this.isMobile = false;
                this.applyStylings(this.isRed ? 'red' : 'sfr');
            }
        });
    }

    ngOnDestroy(): void {
        if (this.selectedLine$) {
            this.selectedLine$.unsubscribe();
        }
    }

    private applyStylings(template: string) {

        const head = document.getElementsByTagName('head')[ 0 ];


        const isCms = this.window.location?.origin.includes('cms')
            || this.window.location?.origin.includes('localhost')
            || this.window.location?.origin.includes('pfv');

        const env = isCms ? 'cms' : 'prod';
        const urls = template === 'red' ? redCssUrls[ env ] : sfrCssUrls[ env ];


        const base = document.createElement('link');
        base.rel = 'stylesheet';
        base.href = urls.base;


        const extra = document.createElement('link');
        extra.rel = 'stylesheet';
        extra.href = urls.plus;

        head.appendChild(base);
        head.appendChild(extra);
    }

    protected readonly MessageType = MessageType;
}
